"use client";

import { useEffect, useState } from "react";
import { highlightCliInHtml } from "@/lib/highlight-cli";
import { BookOpen, Eye, Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import CopyableCodeContent from "@/components/ui/CopyableCodeContent";
import ContentViewTracker from "@/components/site/content-view-tracker";
import FloatingLikeButton from "@/components/site/floating-like-button";
import { formatLikes } from "@/lib/blog-likes";
import { formatViews } from "@/lib/blog-views";
import { formatReadingTime } from "@/lib/blog-reading-time";

type BlogSection = {
	id: number;
	sectionKey: string;
	label: string;
	title: string;
	content: string;
	displayOrder: number;
};

type BlogPost = {
	id: number;
	title: string;
	slug: string;
	excerpt: string;
	date: string;
	readingTimeMinutes: number;
	viewsCount?: number;
	likesCount: number;
	topics: string[];
	imageUrl: string;
	bannerImageUrl: string;
	type?: "BLOG" | "MODULE";
	sections: BlogSection[];
};

const systemFontStyle = {
	fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

function SectionContent({ html }: { html: string }) {
	return (
		<CopyableCodeContent
			className="prose-blog mt-4 max-w-3xl"
			style={{ fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
			html={highlightCliInHtml(html)}
		/>
	);
}

export default function ContentStateEventHandler({ post }: { post: BlogPost }) {
	const [activeSection, setActiveSection] = useState<string>(post.sections[0]?.sectionKey ?? "");
	const [viewsCount, setViewsCount] = useState(post.viewsCount ?? 0);
	const [likesCount, setLikesCount] = useState(post.likesCount);

	useEffect(() => {
		if (post.sections.length === 0) return;

		const headings = post.sections
			.map((s) => document.getElementById(s.sectionKey))
			.filter((el): el is HTMLElement => Boolean(el));

		const onScroll = () => {
			const scrollY = window.scrollY + window.innerHeight * 0.25;
			let current = headings[0]?.id ?? "";
			for (const el of headings) {
				if (el.offsetTop <= scrollY) current = el.id;
			}
			setActiveSection(current);
		};

		window.addEventListener("scroll", onScroll, { passive: true });
		onScroll();

		return () => window.removeEventListener("scroll", onScroll);
	}, [post.sections]);

	return (
		<div className="min-h-screen bg-[#0b0d12] text-zinc-100">
			{post.type && (
				<>
					<ContentViewTracker
						slug={post.slug}
						type={post.type}
						onViewsCount={setViewsCount}
					/>
					<FloatingLikeButton
						slug={post.slug}
						type={post.type}
						initialLikesCount={post.likesCount}
						onLikesCount={setLikesCount}
					/>
				</>
			)}
			<header className="relative overflow-hidden bg-[#0b0d12]">
				<img
					src={post.bannerImageUrl}
					alt=""
					aria-hidden="true"
					className="absolute inset-x-0 top-0 h-[16rem] w-full object-cover object-center opacity-42 sm:h-[18rem] lg:h-[20rem]"
				/>
				<div className="absolute inset-x-0 top-0 h-[20rem] bg-[linear-gradient(180deg,rgba(11,13,18,0.36)_0%,rgba(11,13,18,0.58)_38%,rgba(11,13,18,0.94)_76%,#0b0d12_100%)] sm:h-[22rem] lg:h-[24rem]" />
				<div className="absolute inset-x-0 top-0 h-[20rem] bg-[linear-gradient(90deg,rgba(11,13,18,0.88)_0%,rgba(11,13,18,0.62)_42%,rgba(11,13,18,0.34)_100%)] sm:h-[22rem] lg:h-[24rem]" />

				<div className="relative mx-auto w-full max-w-6xl px-4 pb-10 pt-[15rem] sm:px-6 sm:pb-12 sm:pt-[17rem] lg:pt-[19rem]">
					{post.topics[0] && (
						<span className="font-rethink inline-flex rounded-md bg-white/[0.06] px-2.5 py-1 text-xs font-medium text-zinc-400 backdrop-blur">
							{post.topics[0]}
						</span>
					)}
					<h1 className="font-rethink mt-8 max-w-4xl text-4xl font-semibold text-white sm:text-5xl lg:text-5xl">
						{post.title}
					</h1>
					<p className="mt-4 max-w-3xl text-base font-normal leading-7 text-[#A3A3A3]">
						{post.excerpt}
					</p>
					<div className="mt-12 flex items-center gap-4">
						<img
							src="https://eqwkxvz36vnuod1e.public.blob.vercel-storage.com/portfolio/images/me-XhJ7rDh9AeSRtF2D4axAcIYS6v5Iq7.png"
							alt="Muhammad Nurfatkhur Rahman"
							className="h-11 w-11 rounded-full object-cover"
						/>
						<div>
							<p className="text-base font-medium text-zinc-100">
								Muhammad Nurfatkhur Rahman
							</p>
							<p className="mt-0.5 text-sm text-[#A3A3A3]">{post.date}</p>
						</div>
					</div>
					<div className="mt-12 border-y border-white/10 py-4">
						<div className="flex items-center justify-between gap-3 text-xs text-[#A3A3A3] sm:text-sm">
							<span className="inline-flex items-center gap-1.5 sm:gap-2">
								<Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
								{formatViews(viewsCount)}
							</span>
							<div className="flex shrink-0 items-center gap-3 sm:gap-6">
								<span className="inline-flex items-center gap-1.5 sm:gap-2">
									<BookOpen className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
									{formatReadingTime(post.readingTimeMinutes)}
								</span>
								<span className="inline-flex items-center gap-1.5 sm:gap-2">
									<Heart className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
									{formatLikes(likesCount)}
								</span>
							</div>
						</div>
					</div>
				</div>
			</header>

			<div className="relative mx-auto max-w-6xl px-4 pb-24 pt-12 sm:px-6">
				<div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-start">
					<main className="min-w-0 space-y-10 lg:pr-4">
						{post.sections.map((section) => (
							<section
								key={section.sectionKey}
								id={section.sectionKey}
								className="scroll-mt-28 pt-7 first:pt-0"
							>
								<h2 className="font-rethink max-w-3xl text-2xl font-semibold text-white">
									{section.title}
								</h2>
								<SectionContent html={section.content} />
							</section>
						))}
					</main>

					<aside className="hidden lg:sticky lg:top-28 lg:block lg:h-fit lg:self-start">
						<div className="rounded-lg border border-white/[0.06] bg-transparent p-5">
							<p className="font-rethink text-sm font-medium text-zinc-100">Table of Contents</p>
							<nav className="mt-4 space-y-3">
								{post.sections.map((section) => (
									<a
										key={section.sectionKey}
										href={`#${section.sectionKey}`}
										className={cn(
											"block text-sm font-medium leading-6 transition-colors duration-200",
											activeSection === section.sectionKey
												? "text-zinc-100"
												: "text-zinc-600 hover:text-zinc-300",
										)}
									>
										{section.label}
									</a>
								))}
							</nav>
						</div>
					</aside>
				</div>
			</div>
		</div>
	);
}
